/**
 * Device-local "known kids" — the minimal child profiles (id + display name + avatar) cached on THIS
 * device after a guardian signs in, so the kid-login picker can render "Who's playing?" without a
 * public members endpoint. This is the family-tablet model: a guardian sets the device up, then kids
 * tap their face + PIN.
 *
 * Data minimization (children's product): we store only what the picker needs (never PII beyond the
 * display name the guardian already chose, never credentials), it never leaves the device, and it is
 * only ever populated from a guardian session that already had the right to see these members.
 * Impersonation sessions (ADR-0055) are excluded: an operator acting as a household presents as a
 * guardian of the TARGET household, but must never persist that family's children on their device.
 */
const STORAGE_KEY = 'kidshome.knownKids';

export interface KnownKid {
  readonly id: string;
  readonly displayName: string;
  /** PRESET art key only (an uploaded photo is auth-gated, never cached pre-auth — ADR-0037). */
  readonly avatar?: string;
}

export interface KnownKids {
  readonly householdId: string;
  readonly kids: readonly KnownKid[];
}

/** Read the kids remembered on this device, or null if none / storage is unavailable. */
export function getKnownKids(): KnownKids | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KnownKids;
    if (
      typeof parsed?.householdId !== 'string' ||
      !Array.isArray(parsed.kids) ||
      !parsed.kids.every((k) => k && typeof k.id === 'string' && typeof k.displayName === 'string')
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Replace this device's remembered kids wholesale (scoped to one household at a time). */
export function rememberKids(value: KnownKids): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage may be unavailable (private mode / quota); the picker just falls back to its empty state.
  }
}

/** Forget the remembered kids (e.g. when resetting a shared device). */
export function clearKnownKids(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — nothing to clear if storage is unavailable.
  }
}
